// src/components/DoppeltrefferPanel.jsx
//
// Warnung auf der Startseite der Siegerehrung: Ein Team hat mehrere Preise
// erreicht (z. B. 1. Mixed und 3. gesamt), bekommt aber nur einen. Das
// Orga-Team legt hier VOR dem Start fest, welchen – der andere Preis rueckt in
// seiner Wertung nach. Solange etwas offen ist, laesst sich die Siegerehrung
// nicht starten.
//
// Die Details (Teamnamen, Plaetze) sind standardmaessig eingeklappt, damit die
// Startseite auf dem Beamer nichts verraet. Die Logik steckt vollstaendig in
// utils/preisvergabe.js.
import { AlertTriangle, CheckCircle2, EyeOff, ArrowRight, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { slotBeschreibung } from "../utils/preisvergabe";

function Folge({ folge }) {
  const { slot, nachher } = folge;
  return (
    <li className="flex flex-wrap items-baseline gap-x-1.5">
      <span className="font-semibold text-gray-200">„{slot.preis.title}“</span>
      <span className="text-gray-500">({slotBeschreibung(slot)})</span>
      <ArrowRight className="w-3.5 h-3.5 self-center text-gray-500" />
      {nachher.length === 0 ? (
        <span className="italic text-gray-500">wird nicht vergeben</span>
      ) : nachher.length > 3 ? (
        // Viele gleichauf (typisch: Teams ohne Punkte) – Namen nur im Tooltip
        <span title={nachher.map((n) => n.team.team_name).join(", ")}>
          <span className="text-yellow-300">{nachher.length} Teams gleichauf</span>{" "}
          <span className="text-gray-500">(bisher {nachher[0].bisher}) – geteilter Platz</span>
        </span>
      ) : (
        // Mehrere Nachruecker = geteilter Platz, beide bekommen den Preis
        nachher.map((n, i) => (
          <span key={i}>
            {i > 0 && <span className="text-gray-500">und </span>}
            <span className="text-primary-300">{n.team.team_name}</span>{" "}
            <span className="text-gray-500">(bisher {n.bisher})</span>
            {n.weitererDoppeltreffer && (
              <span className="block text-xs text-yellow-400">
                ⚠ hat dann selbst einen Doppeltreffer – muss danach entschieden werden
              </span>
            )}
          </span>
        ))
      )}
    </li>
  );
}

// `detailsErlaubt` = false in der Beamer-Ansicht der Siegerehrung: dann
// lassen sich die Details (Teamnamen, Plaetze) gar nicht erst aufklappen.
export default function DoppeltrefferPanel({
  verteilung,
  aufgeklappt,
  onUmschalten,
  onWahl,
  onZuruecksetzen,
  detailsErlaubt = true,
}) {
  const { offen, entschieden } = verteilung;
  if (!offen && entschieden.length === 0) return null;

  return (
    <div
      className={`w-full max-w-2xl text-left rounded-lg border mb-8 ${
        offen ? "border-red-700 bg-red-900/20" : "border-green-700 bg-green-900/20"
      }`}
    >
      {/* Kopf: ohne Teamnamen, damit er auch auf dem Beamer nichts verraet */}
      <div className="flex items-start gap-3 p-4">
        {offen ? (
          <AlertTriangle className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
        ) : (
          <CheckCircle2 className="w-6 h-6 text-green-400 shrink-0 mt-0.5" />
        )}
        <div className="flex-1">
          {offen ? (
            <>
              <p className="font-bold text-red-200">Doppeltreffer – Entscheidung des Orga-Teams nötig</p>
              <p className="text-sm text-red-100/80 mt-1">
                Mindestens ein Team hat mehrere Preise erreicht, kann aber nur einen bekommen.
                Das Orga-Team muss vor dem Start festlegen, welchen – der andere Preis geht an
                das nächste Team dieser Wertung. Vorher lässt sich die Siegerehrung nicht starten.
              </p>
            </>
          ) : (
            <>
              <p className="font-bold text-green-200">Doppeltreffer entschieden</p>
              <p className="text-sm text-green-100/80 mt-1">
                {entschieden.length === 1
                  ? "1 Doppeltreffer wurde vom Orga-Team entschieden."
                  : `${entschieden.length} Doppeltreffer wurden vom Orga-Team entschieden.`}{" "}
                Die Siegerehrung kann starten.
              </p>
            </>
          )}
          {detailsErlaubt ? (
          <button
            type="button"
            onClick={onUmschalten}
            className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary-400 hover:text-primary-300"
          >
            {aufgeklappt ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {aufgeklappt ? "Details ausblenden" : offen ? "Jetzt entscheiden" : "Entscheidungen anzeigen"}
          </button>
          ) : (
            <p className="mt-3 flex items-center gap-1.5 text-sm text-gray-400">
              <EyeOff className="w-4 h-4 shrink-0" />
              {offen
                ? "Zum Entscheiden oben die Anzeige „Nur Orga-Team“ wählen."
                : "Details nur in der Anzeige „Nur Orga-Team“."}
            </p>
          )}
        </div>
      </div>

      {aufgeklappt && detailsErlaubt && (
        <div className="border-t border-secondary-800 bg-secondary-900 rounded-b-lg p-4">
          <p className="flex items-center gap-1.5 text-xs text-red-300 mb-4">
            <EyeOff className="w-3.5 h-3.5 shrink-0" />
            Nur für das Orga-Team – nicht auf dem Beamer zeigen, sonst sind die Plätze verraten.
          </p>

          {offen && (
            <>
              <p className="mb-3 text-gray-300">
                <strong className="text-primary-400">{offen.team.team_name}</strong> hat{" "}
                {offen.optionen.length} Preise erreicht. Welchen Preis vergibt das Orga-Team an
                dieses Team?
              </p>
              <div className="space-y-3">
                {offen.optionen.map((o) => (
                  <button
                    key={o.slot.id}
                    type="button"
                    onClick={() => onWahl(offen.key, o.slot.id)}
                    className="w-full text-left rounded-lg border border-secondary-700 bg-secondary-950 hover:border-primary-500 hover:bg-secondary-800 transition-colors p-4"
                  >
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-lg font-bold text-yellow-400">🎁 {o.slot.preis.title}</span>
                      {o.slot.preis.sponsor && (
                        <span className="text-sm text-gray-400">von {o.slot.preis.sponsor}</span>
                      )}
                      <span className="text-sm text-gray-400">
                        {slotBeschreibung(o.slot)}
                        {o.bisherPlatz !== o.slot.platz && ` – nachgerückt von ${o.bisherPlatz}.`}
                      </span>
                    </div>
                    {o.slot.preis.description && (
                      <p className="text-sm text-gray-400 mt-0.5">{o.slot.preis.description}</p>
                    )}
                    {o.folgen.length > 0 && (
                      <div className="mt-3 text-sm">
                        <p className="text-gray-500 mb-1">Dann rückt nach:</p>
                        <ul className="space-y-1">
                          {o.folgen.map((f) => (
                            <Folge key={f.slot.id} folge={f} />
                          ))}
                        </ul>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}

          {entschieden.length > 0 && (
            <div className={offen ? "mt-5 border-t border-secondary-800 pt-4" : ""}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-400">Bereits entschieden</p>
                <button
                  type="button"
                  onClick={onZuruecksetzen}
                  className="flex items-center gap-1 text-sm text-gray-400 hover:text-white"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Neu entscheiden
                </button>
              </div>
              <ul className="space-y-1 text-sm">
                {entschieden.map((e) => (
                  <li key={e.key}>
                    <span className="text-primary-300">{e.team.team_name}</span> bekommt „
                    {e.gewaehlt.preis.title}“
                    <span className="text-gray-500">
                      {" "}
                      statt {e.verzicht.map((s) => `„${s.preis.title}“ (${slotBeschreibung(s)})`).join(", ")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
